import { request } from "components/hooks/useQuery";
import { PageSlug } from "types/base";
import { createSubscription } from "components/hooks/usePreview";
import { GetStaticPropsContext } from "next";
import { getAllLegalSectionsByLegalPage, getLegalSectionAndLegalPage } from "queries/Legal";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import React from "react";
import Container from "components/core/Container";
import { withAppLayout } from "components/core/withAppLayout";
import nextI18NextConfig from "./../../../next-i18next.config";
import TextBlock from "blocks/text/TextBlock";
import LegalParagraph from "components/elements/LegalParagraph";
import LegalSidebar from "components/legal/LegalSidebar";
import Table from "blocks/table/Table";
import Content from "components/legal/Content";
import useTrans from "components/hooks/useTrans";

export async function getStaticPaths({ locales }: any) {
    const { legalPage } = await request({
        query: getAllLegalSectionsByLegalPage
    });

    const paths = legalPage.sections.map(({ slug }: any) => {
        return locales.map((locale: any) => ({
            params: { slug: slug }, locale: locale
        })).flat();
    }).flat();

    return {
        paths: paths,
        fallback: false
    };
}

export async function getStaticProps(context: GetStaticPropsContext) {
    const params = context.params as PageSlug;

    const query = {
        query: getLegalSectionAndLegalPage(params.slug, context.locale, context.locales),
        preview: context.preview,
        withAppData: true,
        locale: context.locale
    };

    let data = await createSubscription(context, query);

    if (!data?.initialData?.legalSection) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            data: data,
            appData: {
                ...data.initialData.header,
                ...data.initialData.footer,
            },
            ...(await serverSideTranslations(context.locale, ['common'], nextI18NextConfig)),
        },
    };
}

export interface ILegalSection {
    id: string,
    title: string,
    shortTitle: string,
    slug: string,
    content: any[],
    lastUpdated: string
}

export interface ILegalPage {
    id: string,
    title: string,
    sections: ILegalSection[]
}

type Props = {
    legalPage: ILegalPage,
    legalSection: ILegalSection
};

function LegalSection(props: Props) {
    const { legalPage, legalSection } = props;
    const { t } = useTrans();

    return (
        <div>
            <section className={"w-full"}>
                <TextBlock
                    align={"center"}
                    theme={"gray"}
                    title={legalPage.title}
                    description={""}
                    isHero={true}
                />
            </section>
            
            <section>
                <Container>
                    <div className={"w-full mb-20 mt-20"}>
                        <div className={"flex flex-row w-full"}>
                            <LegalSidebar
                                items={legalPage.sections}
                                current={legalSection.shortTitle}
                            />
                            <div className={'w-full m-0 text-justify'}>
                                <div className={"mb-10"}>
                                    <h2 className={"text-purple-dark font-medium text-3xl sm:text-3xl mb-2"}>
                                        {legalSection.title}
                                    </h2>
                                    <LegalParagraph className={"mb-10 text-right"}>
                                        <i> {t('lastUpdatedOn')} {legalSection.lastUpdated}</i>
                                    </LegalParagraph>
                                    <LegalParagraph>
                                        {legalSection.content.map(block => {
                                            if (block.__typename == "SimpleTextRecord") {
                                                return (
                                                    <div id={block.referenceId}>
                                                        <Content
                                                            key={block.id}
                                                            content={block.text}
                                                            paragraphSize={'medium'}
                                                            paragraphClassName={'text opacity-60'}
                                                        />
                                                    </div>
                                                );
                                            }

                                            if (block.__typename == "TableRecord") {
                                                return <Table
                                                    key={block.id}
                                                    {...block}
                                                />;
                                            }
                                        })}
                                    </LegalParagraph>
                                    <LegalParagraph className={"mt-10"}>
                                        <i> {t('lastUpdatedOn')} {legalSection.lastUpdated}</i>
                                    </LegalParagraph>
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
        </div>
    );
}

export default withAppLayout(LegalSection);